/**
 * Auth domain public API.
 * Only export what other layers should consume.
 */

// Context
export { AuthProvider, useAuth } from './context/auth-context';

// Hooks
export { useAuthApi } from './hooks/use-auth-api';
export { useAuthGuard, useAdminAuthGuard, useSuperAdminAuthGuard } from './hooks/use-auth-guard';

// Guards
export { default as ProtectedRoute } from './components/guards/ProtectedRoute';
export { default as ClientProtectedRoute, useClientAuth } from './components/guards/ClientProtectedRoute';
export { AdminRouteGuard, withAdminGuard, withSuperAdminGuard } from './components/guards/AdminRouteGuard';
export { default as MfaRouteGuard } from './components/guards/MfaRouteGuard';
export { PermissionGuard, AdminOnly, SuperAdminOnly, RoleGuard, PermissionOnly } from './components/guards/PermissionGuard';

// Components
export { default as LoginForm } from './components/forms/LoginForm';
export { default as CredentialAuth } from './components/forms/CredentialAuth';
export { PasskeyRegistrationForm } from './components/forms/PasskeyRegistrationForm';
export { PasskeyManagement } from './components/PasskeyManagement';
export { ConnectionDiagnostics } from './components/ConnectionDiagnostics';
export { default as MFAManagementModal } from './components/mfa/MFAManagementModal';
export { WebAuthnMFASetup } from './components/mfa/WebAuthnMFASetup';

// Pages
export { default as LoginPage } from './pages/LoginPage';
export { default as RegisterPage } from './pages/RegisterPage';
export { default as MFAVerificationPage } from './pages/MFAVerificationPage';
export { default as MFASetupPage } from './pages/MFASetupPage';
export { default as ClientLoginPage } from './pages/ClientLoginPage';
export { default as ClientRegisterPage } from './pages/ClientRegisterPage';
export { default as EmployeeLoginPage } from './pages/EmployeeLoginPage';

// Services
export { authService } from './services/auth-service';
export { emailService } from './services/email-service';
export { default as webauthnService, WebAuthnService } from './services/webauthn-service';
export { SecureAuditLogger } from './services/secure-audit-logging';
export { validateUserAccess, authorizeOperation, getUserSecurityContext } from './services/secure-auth-service';

// Utils
export {
  getAuthConfig,
  generateAuditLog,
  isUserAuthenticated,
  checkSessionContamination,
  cleanupSessionContamination,
  clearAllSessions,
  formatAuthError,
  type Audience,
  type SessionContaminationResult,
  type SessionCleanupResult,
} from './utils/auth-utils';
export { requestSelfMFASetup, getMFASetupInstructions } from './utils/mfa-admin-helper';
export {
  validateToken,
  isTokenExpired,
  getStoredToken,
  getStoredUser,
  storeToken,
  clearStoredToken,
  getTimeUntilStoredTokenExpiry,
  TOKEN_KEYS,
  DEFAULT_EXPIRATION_BUFFER_MS,
  storeMfaUserId,
  getMfaUserId,
  clearMfaUserId,
  getActiveToken,
  decodeTokenPayload,
} from './utils/token-utils';
export {
  storeTokenWithMetadata,
  clearAllTokenStorage,
  type TokenMetadata,
} from './utils/token-storage';
export { ensureTokenValid } from './utils/token-checker';
// Display helpers
export {
  getUserDisplayName,
  getUserGreetingName,
  getUserInitials,
  getUserRoleLabel,
  isClientUser,
  type DisplayUser,
} from './utils/user-display';

// Types
export { UserRole, PermissionLevel, getUserPermissions, canAccessFeature } from './types/security-clearance';
export type { UserPermissions } from './types/security-clearance';
